import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchUserAttendance } from "../redux/actions/AttendanceReportActions";
import dayjs from "dayjs";
import DailyActivityModal from "../pages/Attend/DailyActivityModal";

const AttendanceReport = () => {
  const dispatch = useDispatch();

  const currentMonth = dayjs().format("YYYY-MM");
  const previousMonth = dayjs().subtract(1, "month").format("YYYY-MM");
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const { attendanceData, loading, error } = useSelector((state) => state.attendanceReport);
  const { data: profileData } = useSelector((state) => state.profile);

  useEffect(() => {
    dispatch(fetchUserAttendance(selectedMonth));
  }, [dispatch, selectedMonth]);

  const monthlyData = attendanceData?.[selectedMonth];
  const summary = monthlyData?.summary || {};
  const calendar = monthlyData?.calendar || {};

  const getStatusStyle = (status) => {
    switch (status) {
      case "present":
        return "bg-green-100 text-green-800";
      case "leave":
        return "bg-red-100 text-red-600";
      case "holiday":
        return "bg-yellow-100 text-orange-600";
      case "pending":
        return "bg-blue-100 text-blue-600";
      case "absent":
      default:
        return "bg-gray-300 text-gray-800";
    }
  };

  return (
    <div className="min-h-screen bg-slate-800 text-white flex flex-col">
      {/* Header */}
      <div className="bg-sky-400 p-4 rounded-b-3xl shadow-md flex-shrink-0">
        <div className="flex justify-between items-center">
          <span className="text-sm font-bold">Attendance Report</span>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-24">
        {/* Month Selector */}
        <div className="mt-4 flex justify-end px-4">
          <select
            className="bg-white text-black px-3 py-2 rounded shadow text-sm"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value={currentMonth}>{dayjs(currentMonth).format("MMMM YYYY")}</option>
            <option value={previousMonth}>{dayjs(previousMonth).format("MMMM YYYY")}</option>
          </select>
        </div>

        {/* Summary Section */}
        <div className="bg-white text-black mx-4 mt-4 p-4 rounded-2xl shadow-lg">
          <h2 className="text-lg font-bold mb-4 text-center">Monthly Summary</h2>
          <div className="grid grid-cols-2 gap-3">
            <SummaryCard
              label="Present Days"
              value={summary.total_present_days || 0}
              border="border-green-500"
              text="text-green-600"
            />
            <SummaryCard
              label="Leave Days"
              value={summary.total_leave_days || 0}
              border="border-red-500"
              text="text-red-600"
            />
            <SummaryCard
              label="Absent Days"
              value={summary.total_absent_days || 0}
              border="border-yellow-400"
              text="text-yellow-600"
            />
            <SummaryCard
              label="Pending Days"
              value={summary.total_pending_days || 0}
              border="border-blue-400"
              text="text-blue-600"
            />
            <SummaryCard
              label="Working Hours"
              value={`${summary.total_working_hours || 0}h`}
              border="border-purple-400"
              text="text-purple-600"
            />
            <SummaryCard
              label="Expected Hours"
              value={`${summary.expected_working_hours || 0}h`}
              border="border-indigo-400"
              text="text-indigo-600"
            />
          </div>
        </div>

        {/* Attendance Table */}
        <div className="bg-white text-black mx-4 mt-6 p-4 rounded-2xl shadow-lg">
          <h2 className="text-lg font-bold mb-3">Monthly Attendance</h2>
          {loading && (
            <div className="flex justify-center items-center py-8">
              <p className="text-gray-600">Loading...</p>
            </div>
          )}
          {error && (
            <div className="flex justify-center items-center py-8">
              <p className="text-red-500">{error}</p>
            </div>
          )}
          {!loading && !error && (
            <div className="max-h-[40vh] overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50 border-b">
                  <tr className="text-left text-gray-700">
                    <th className="py-3 px-3 font-semibold">Date</th>
                    <th className="py-3 px-3 font-semibold">Status</th>
                    <th className="py-3 px-3 font-semibold">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(calendar).length === 0 ? (
                    <tr>
                      <td colSpan="3" className="text-center py-8 text-gray-500">
                        No attendance data available for this month
                      </td>
                    </tr>
                  ) : (
                    Object.entries(calendar)
                      .sort(([a], [b]) => new Date(a) - new Date(b))
                      .map(([date, entry]) => {
                        // Support both old (string) and new (object) response shapes
                        const status = typeof entry === "object" ? entry.status : entry;
                        const description = typeof entry === "object" ? entry.description : "";

                        return (
                          <tr
                            key={date}
                            className="border-b cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              setSelectedDate(date);
                              setSelectedStatus(status);
                              setShowModal(true);
                            }}
                          >
                            <td className="py-3 px-3">
                              {dayjs(date).format("ddd, MMM D, YYYY")}
                            </td>
                            <td className="py-3 px-3">
                              <span
                                className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusStyle(status)}`}
                              >
                                {status.charAt(0).toUpperCase() + status.slice(1)}
                              </span>
                            </td>
                            <td className="py-3 px-3 text-gray-500 text-xs">
                              {description || "—"}
                            </td>
                          </tr>
                        );
                      })
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Status Legend */}
        <div className="bg-white text-black mx-4 mt-4 px-4 py-3 rounded-2xl shadow">
          <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-around sm:items-center">
            <Legend label="Present" color="bg-green-500" />
            <Legend label="Leave" color="bg-red-500" />
            <Legend label="Holiday" color="bg-yellow-400" />
            <Legend label="Absent" color="bg-gray-500" />
            <Legend label="Pending" color="bg-blue-400" />
          </div>
        </div>

        <div className="h-4"></div>
      </div>

      {/* Modal */}
      {showModal && (
        <DailyActivityModal
          onClose={() => setShowModal(false)}
          user={profileData?.name}
          date={dayjs(selectedDate).format("dddd, MMMM DD, YYYY")}
          status={selectedStatus}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, border, text }) => (
  <div className={`p-3 border-l-4 ${border} rounded shadow bg-gray-50`}>
    <p className="text-xs text-gray-600 mb-1">{label}</p>
    <p className={`text-lg font-bold ${text}`}>{value}</p>
  </div>
);

const Legend = ({ label, color }) => (
  <div className="flex items-center space-x-2">
    <span className={`w-3 h-3 ${color} rounded-full flex-shrink-0`} />
    <span className="text-sm font-medium">{label}</span>
  </div>
);

export default AttendanceReport;